import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  applyDetailTarget,
  parseDetailTarget,
  type DetailTarget,
} from '../shared/detail-route'
import type {
  ApplicationSnapshot,
  DashboardSnapshot,
  HealthState,
  InfrastructureSnapshot,
} from '../shared/types'
import { MONITOR_REFRESH_INTERVAL_MS } from '../shared/timing'
import { availableExternalMetric, tractionScores } from '../shared/product-metrics'
import { ApiError, api } from './api'
import {
  ApplicationCard,
  DetailSheet,
  EmptyState,
  FleetHero,
  InfrastructurePanel,
  MetricRail,
  ProductMetricRail,
  NotificationBadge,
  StatusDot,
} from './components'
import { clockTime, relativeTime } from './format'
import {
  LogoutIcon,
  PulseIcon,
  RefreshIcon,
  SearchIcon,
  ServerIcon,
  TrafficIcon,
} from './icons'
import { TrafficPanel } from './Traffic'
import { Login } from './Login'

type SessionState = 'loading' | 'authenticated' | 'anonymous'
type FilterState = 'all' | HealthState
type DashboardView = 'apps' | 'machine' | 'traffic'
type ApplicationLens = 'health' | 'traction'
type ApplicationSort =
  | 'health'
  | 'latest-deployment'
  | 'first-deployed-newest'
  | 'first-deployed-oldest'
  | 'name'
  | 'traction'
  | 'growth'
  | 'active-users'
  | 'core-activity'

function viewFromParams(hasApplication: boolean, view: string | null): DashboardView {
  if (hasApplication) return 'apps'
  return view === 'machine' || view === 'traffic' ? view : 'apps'
}

const STATUS_ORDER: Record<HealthState, number> = {
  outage: 0,
  degraded: 1,
  unknown: 2,
  maintenance: 3,
  operational: 4,
}

export default function App() {
  const [dashboardView, setDashboardView] = useState<DashboardView>(() => {
    const params = new URLSearchParams(window.location.search)
    return viewFromParams(params.has('app'), params.get('view'))
  })
  const [applicationLens, setApplicationLens] = useState<ApplicationLens>('health')
  const [session, setSession] = useState<SessionState>('loading')
  const [snapshot, setSnapshot] = useState<DashboardSnapshot>()
  const [infrastructure, setInfrastructure] = useState<InfrastructureSnapshot>()
  const [infrastructureRange, setInfrastructureRange] =
    useState<InfrastructureSnapshot['range']>('24h')
  const [selected, setSelected] = useState<ApplicationSnapshot>()
  const [focusedComponentId, setFocusedComponentId] = useState<string>()
  const [focusedIncidentId, setFocusedIncidentId] = useState<string>()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterState>('all')
  const [sort, setSort] = useState<ApplicationSort>('health')
  const [scanning, setScanning] = useState(false)
  const [testingNotification, setTestingNotification] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [, tick] = useState(0)

  const loadInfrastructure = useCallback(
    async (
      range: InfrastructureSnapshot['range'] = infrastructureRange,
      silent = true,
    ) => {
      try {
        setInfrastructure(await api.infrastructure(range))
      } catch (caught) {
        if (!silent) {
          setError(
            caught instanceof Error
              ? caught.message
              : 'Relay could not load machine history.',
          )
        }
      }
    },
    [infrastructureRange],
  )

  const loadSnapshot = useCallback(async (manual = false, silent = false) => {
    if (manual) {
      setScanning(true)
      setNotice('Running a complete fleet scan. Current results remain available.')
    }
    if (!silent) setError('')
    try {
      const next = manual ? await api.run() : await api.status()
      setSnapshot(next)
      setSession('authenticated')
      const linkedTarget = parseDetailTarget(window.location.search)
      setFocusedComponentId(linkedTarget?.componentId)
      setFocusedIncidentId(linkedTarget?.incidentId)
      setSelected((current) => {
        const applicationId = linkedTarget?.applicationId ?? current?.application.id
        return applicationId
          ? next.applications.find(
              (application) => application.application.id === applicationId,
            )
          : undefined
      })
      if (manual) setNotice('Fleet scan completed. The dashboard is up to date.')
      if (manual && dashboardView === 'machine') {
        void loadInfrastructure(infrastructureRange)
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.problem.status === 401) {
        setSession('anonymous')
        setSnapshot(undefined)
      } else {
        setSession((current) => (current === 'loading' ? 'authenticated' : current))
        if (!silent) {
          setError(caught instanceof Error ? caught.message : 'Relay could not update the fleet.')
        }
        if (manual) setNotice('')
      }
    } finally {
      if (manual) setScanning(false)
    }
  }, [dashboardView, infrastructureRange, loadInfrastructure])

  useEffect(() => {
    loadSnapshot()
  }, [loadSnapshot])

  useEffect(() => {
    if (session !== 'authenticated') return
    if (dashboardView === 'machine') {
      loadInfrastructure(infrastructureRange, false)
    }
    const poll = window.setInterval(() => {
      loadSnapshot(false, true)
      if (dashboardView === 'machine') {
        loadInfrastructure(infrastructureRange)
      }
    }, MONITOR_REFRESH_INTERVAL_MS)
    const clock = window.setInterval(() => tick((value) => value + 1), 1_000)
    const syncVisibleSnapshot = () => {
      if (document.visibilityState === 'visible') loadSnapshot(false, true)
    }
    document.addEventListener('visibilitychange', syncVisibleSnapshot)
    return () => {
      window.clearInterval(poll)
      window.clearInterval(clock)
      document.removeEventListener('visibilitychange', syncVisibleSnapshot)
    }
  }, [
    infrastructureRange,
    dashboardView,
    loadInfrastructure,
    loadSnapshot,
    session,
  ])

  useEffect(() => {
    const syncDetailRoute = () => {
      const target = parseDetailTarget(window.location.search)
      const requestedView = new URLSearchParams(window.location.search).get('view')
      setDashboardView(viewFromParams(Boolean(target), requestedView))
      setFocusedComponentId(target?.componentId)
      setFocusedIncidentId(target?.incidentId)
      setSelected(
        target && snapshot
          ? snapshot.applications.find(
              (application) =>
                application.application.id === target.applicationId,
            )
          : undefined,
      )
    }
    window.addEventListener('popstate', syncDetailRoute)
    return () => window.removeEventListener('popstate', syncDetailRoute)
  }, [snapshot])

  function setDetailRoute(target?: DetailTarget) {
    const url = applyDetailTarget(new URL(window.location.href), target)
    window.history.replaceState({}, '', url)
  }

  function openApplication(application: ApplicationSnapshot) {
    const url = applyDetailTarget(new URL(window.location.href), {
      applicationId: application.application.id,
    })
    url.searchParams.delete('view')
    window.history.replaceState({}, '', url)
    setDashboardView('apps')
    setFocusedComponentId(undefined)
    setFocusedIncidentId(undefined)
    setSelected(application)
  }

  function closeApplication() {
    setDetailRoute()
    setFocusedComponentId(undefined)
    setFocusedIncidentId(undefined)
    setSelected(undefined)
  }

  function showDashboardView(view: DashboardView) {
    const url = applyDetailTarget(new URL(window.location.href))
    if (view === 'apps') url.searchParams.delete('view')
    else url.searchParams.set('view', view)
    window.history.replaceState({}, '', url)
    setSelected(undefined)
    setFocusedComponentId(undefined)
    setFocusedIncidentId(undefined)
    setDashboardView(view)
  }

  async function logout() {
    await api.logout()
    setSession('anonymous')
    setSnapshot(undefined)
  }

  async function testNotification() {
    if (testingNotification) return
    setTestingNotification(true)
    setError('')
    setNotice('')
    try {
      const result = await api.testNotification()
      if (result.delivered) {
        setNotice('Test notification delivered to ntfy.')
        await loadSnapshot(false)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The test notification failed.')
    } finally {
      setTestingNotification(false)
    }
  }

  const traction = useMemo(
    () => tractionScores(snapshot?.applications ?? []),
    [snapshot],
  )

  const applications = useMemo(() => {
    if (!snapshot) return []
    const query = search.trim().toLowerCase()
    return [...snapshot.applications]
      .filter((application) => application.application.kind !== 'infrastructure')
      .filter(
        (application) =>
          application.asleep || filter === 'all' || application.status === filter,
      )
      .filter((application) => {
        if (!query) return true
        return [
          application.application.name,
          application.application.description,
          ...(application.application.tags ?? []),
          ...(application.application.domains ?? []),
          ...(application.application.domainInventory?.flatMap((domain) => [
            domain.hostname,
            ...domain.sources,
          ]) ?? []),
          ...(application.application.technologies?.flatMap((technology) => [
            technology.name,
            technology.category,
            technology.confidence,
            ...technology.sources,
          ]) ?? []),
          ...(application.productMetrics?.metrics.flatMap((metric) => [
            metric.id,
            metric.name,
            metric.description,
            metric.semantic,
          ]) ?? []),
          application.application.deployment?.first?.at,
          application.application.deployment?.first?.evidence,
          application.application.deployment?.latest?.at,
          application.application.deployment?.latest?.evidence,
        ]
          .join(' ')
          .toLowerCase()
          .includes(query)
      })
      .sort((left, right) => {
        const nameOrder = left.application.name.localeCompare(
          right.application.name,
        )
        if (sort === 'name') return nameOrder
        if (sort === 'health') {
          return (
            STATUS_ORDER[left.status] - STATUS_ORDER[right.status] || nameOrder
          )
        }
        if (sort === 'traction') {
          const leftScore = traction.get(left.application.id)?.score
          const rightScore = traction.get(right.application.id)?.score
          if (leftScore === null || leftScore === undefined) {
            return rightScore === null || rightScore === undefined ? nameOrder : 1
          }
          if (rightScore === null || rightScore === undefined) return -1
          return rightScore - leftScore || nameOrder
        }
        if (sort === 'growth') {
          const leftGrowth = availableExternalMetric(left, 'active-users', '7d')?.change7d?.percent
          const rightGrowth = availableExternalMetric(right, 'active-users', '7d')?.change7d?.percent
          if (leftGrowth == null) return rightGrowth == null ? nameOrder : 1
          if (rightGrowth == null) return -1
          return rightGrowth - leftGrowth || nameOrder
        }
        if (sort === 'active-users' || sort === 'core-activity') {
          const semantic = sort === 'active-users' ? 'active-users' : 'core-action'
          const leftValue = availableExternalMetric(left, semantic, '7d')?.value
          const rightValue = availableExternalMetric(right, semantic, '7d')?.value
          if (leftValue === undefined) return rightValue === undefined ? nameOrder : 1
          if (rightValue === undefined) return -1
          return rightValue - leftValue || nameOrder
        }
        const field = sort === 'latest-deployment' ? 'latest' : 'first'
        const leftAt = left.application.deployment?.[field]?.at
        const rightAt = right.application.deployment?.[field]?.at
        if (!leftAt && !rightAt) return nameOrder
        if (!leftAt) return 1
        if (!rightAt) return -1
        const direction = sort === 'first-deployed-oldest' ? 1 : -1
        return (Date.parse(leftAt) - Date.parse(rightAt)) * direction || nameOrder
      })
  }, [filter, search, snapshot, sort, traction])

  if (session === 'loading') {
    return (
      <main className="boot-screen">
        <div className="brand-mark boot-mark"><PulseIcon /></div>
        <span className="button-spinner" />
        <p>Establishing a private signal…</p>
      </main>
    )
  }

  if (session === 'anonymous') {
    return (
      <Login
        onAuthenticated={() => {
          setSession('loading')
          loadSnapshot()
        }}
      />
    )
  }

  if (!snapshot) {
    return (
      <main className="boot-screen">
        <div className="brand-mark boot-mark"><PulseIcon /></div>
        <span className="button-spinner" />
        <p>Loading the latest saved snapshot…</p>
        {error && <button onClick={() => loadSnapshot()}>Try again</button>}
      </main>
    )
  }

  const allApplications = snapshot.applications.filter(
    (application) => application.application.kind !== 'infrastructure',
  )
  // Sleeping apps are deliberately stopped, so they are neither healthy nor
  // unhealthy. They get their own section below and stay out of fleet counts.
  const applicationFleet = allApplications.filter((application) => !application.asleep)
  const awakeApplications = applications.filter((application) => !application.asleep)
  const sleepingApplications = applications.filter((application) => application.asleep)
  const sleepingTotal = allApplications.length - applicationFleet.length
  const machineApplication = snapshot.applications.find(
    (application) => application.application.kind === 'infrastructure',
  )
  const machineStatus =
    infrastructure?.status ?? machineApplication?.status ?? 'unknown'
  const applicationStatus = applicationFleet.reduce<HealthState>(
    (worst, application) =>
      STATUS_ORDER[application.status] < STATUS_ORDER[worst]
        ? application.status
        : worst,
    'operational',
  )
  const filters: Array<{ id: FilterState; label: string; count: number }> = [
    { id: 'all', label: 'All systems', count: applicationFleet.length },
    {
      id: 'outage',
      label: 'Outage',
      count: applicationFleet.filter((application) => application.status === 'outage').length,
    },
    {
      id: 'degraded',
      label: 'Degraded',
      count: applicationFleet.filter((application) => application.status === 'degraded').length,
    },
    {
      id: 'operational',
      label: 'Operational',
      count: applicationFleet.filter((application) => application.status === 'operational').length,
    },
  ]

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Relay home">
          <span className="brand-mark"><PulseIcon /></span>
          <span>
            <strong>{snapshot.product.title}</strong>
            <small>{snapshot.product.subtitle}</small>
          </span>
        </a>
        <div className="topbar-actions">
          <NotificationBadge
            snapshot={snapshot}
            onTest={testNotification}
            testing={testingNotification}
          />
          <button
            className="refresh-button"
            onClick={() => loadSnapshot(true)}
            disabled={scanning}
            aria-label={scanning ? 'Scanning systems' : 'Rescan systems'}
          >
            <RefreshIcon className={scanning ? 'spin' : ''} />
            <span>{scanning ? 'Scanning' : 'Rescan'}</span>
          </button>
          <button className="icon-button logout-button" onClick={logout} aria-label="Sign out">
            <LogoutIcon />
          </button>
        </div>
      </header>

      <main className="dashboard">
        {error && (
          <div className="toast error-toast" role="alert">
            <StatusDot state="outage" />
            <span>{error}</span>
            <button onClick={() => setError('')}>Dismiss</button>
          </div>
        )}
        {notice && (
          <div className="toast success-toast" role="status">
            <StatusDot state="operational" />
            <span>{notice}</span>
            <button onClick={() => setNotice('')}>Dismiss</button>
          </div>
        )}

        <nav className="dashboard-view-tabs" aria-label="Dashboard views">
          <button
            className={dashboardView === 'apps' ? 'active' : ''}
            onClick={() => showDashboardView('apps')}
            aria-current={dashboardView === 'apps' ? 'page' : undefined}
          >
            <PulseIcon />
            <span>
              <strong>Applications</strong>
              <small>{applicationFleet.length} products</small>
            </span>
            <StatusDot state={applicationStatus} />
          </button>
          <button
            className={dashboardView === 'machine' ? 'active' : ''}
            onClick={() => showDashboardView('machine')}
            aria-current={dashboardView === 'machine' ? 'page' : undefined}
          >
            <ServerIcon />
            <span>
              <strong>Machine</strong>
              <small>VPS resources & guidance</small>
            </span>
            <StatusDot state={machineStatus} />
          </button>
          <button
            className={dashboardView === 'traffic' ? 'active' : ''}
            onClick={() => showDashboardView('traffic')}
            aria-current={dashboardView === 'traffic' ? 'page' : undefined}
          >
            <TrafficIcon />
            <span>
              <strong>Traffic</strong>
              <small>Visitors, logs & sleep</small>
            </span>
          </button>
        </nav>

        {dashboardView === 'apps' ? (
          <>
            <FleetHero snapshot={snapshot} />
            {applicationLens === 'health' ? (
              <MetricRail snapshot={snapshot} />
            ) : (
              <ProductMetricRail snapshot={snapshot} scores={traction} />
            )}

            <section className="systems-section">
              <header className="systems-head">
                <div>
                  <span className="section-kicker">Application fleet</span>
                  <h2>
                    {applicationLens === 'health'
                      ? 'Every product, one signal'
                      : 'Where traction is compounding'}
                  </h2>
                </div>
                <div className="systems-controls">
                  <div className="lens-toggle" aria-label="Application data lens">
                    <button
                      className={applicationLens === 'health' ? 'active' : ''}
                      onClick={() => setApplicationLens('health')}
                      aria-pressed={applicationLens === 'health'}
                    >
                      Health
                    </button>
                    <button
                      className={applicationLens === 'traction' ? 'active' : ''}
                      onClick={() => {
                        setApplicationLens('traction')
                        if (sort === 'health') setSort('traction')
                      }}
                      aria-pressed={applicationLens === 'traction'}
                    >
                      Traction
                    </button>
                  </div>
                  <div className="search-field">
                    <SearchIcon />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search systems"
                      aria-label="Search systems"
                    />
                  </div>
                  <label className="sort-field">
                    <span>Sort</span>
                    <select
                      value={sort}
                      onChange={(event) =>
                        setSort(event.target.value as ApplicationSort)
                      }
                      aria-label="Sort applications"
                    >
                      <option value="health">Needs attention</option>
                      <option value="traction">Traction score</option>
                      <option value="growth">Fastest growth</option>
                      <option value="active-users">Active users · 7d</option>
                      <option value="core-activity">Core activity · 7d</option>
                      <option value="latest-deployment">Latest deployment</option>
                      <option value="first-deployed-newest">First deployed · newest</option>
                      <option value="first-deployed-oldest">First deployed · oldest</option>
                      <option value="name">Name</option>
                    </select>
                  </label>
                </div>
              </header>

              <nav className="filter-tabs" aria-label="Filter applications">
                {filters.map((item) => (
                  <button
                    className={filter === item.id ? 'active' : ''}
                    key={item.id}
                    onClick={() => setFilter(item.id)}
                  >
                    {item.label}
                    <span>{item.count}</span>
                  </button>
                ))}
              </nav>

              <div className="application-grid">
                {awakeApplications.map((application) => (
                  <ApplicationCard
                    key={application.application.id}
                    application={application}
                    history={snapshot.history[application.application.id] ?? []}
                    lens={applicationLens}
                    traction={traction.get(application.application.id)}
                    onOpen={() => openApplication(application)}
                  />
                ))}
              </div>
              {!awakeApplications.length && !sleepingApplications.length && (
                <EmptyState filter={search || filter} />
              )}
            </section>

            {sleepingTotal > 0 && (
              <section className="systems-section sleeping-section" aria-label="Sleeping applications">
                <header className="systems-head">
                  <div>
                    <span className="section-kicker">Sleeping · {sleepingTotal}</span>
                    <h2>Stopped while idle</h2>
                    <p className="sleeping-note">
                      Not probed and not counted in the fleet above. Each wakes on its next visit.
                      Compare these with the awake apps and change the choice in{' '}
                      <button type="button" className="link-button" onClick={() => showDashboardView('traffic')}>
                        Traffic and sleep
                      </button>
                      .
                    </p>
                  </div>
                </header>
                <div className="application-grid sleeping-grid">
                  {sleepingApplications.map((application) => (
                    <ApplicationCard
                      key={application.application.id}
                      application={application}
                      history={snapshot.history[application.application.id] ?? []}
                      lens={applicationLens}
                      traction={traction.get(application.application.id)}
                      onOpen={() => openApplication(application)}
                    />
                  ))}
                </div>
                {!sleepingApplications.length && (
                  <p className="sleeping-note">No sleeping application matches the search.</p>
                )}
              </section>
            )}
          </>
        ) : dashboardView === 'traffic' ? (
          <TrafficPanel />
        ) : (
          <InfrastructurePanel
            snapshot={infrastructure}
            range={infrastructureRange}
            onRangeChange={(range) => {
              setInfrastructureRange(range)
              void loadInfrastructure(range, false)
            }}
            onOpenDetails={() => {
              if (machineApplication) openApplication(machineApplication)
            }}
          />
        )}
      </main>

      <footer className="dashboard-footer">
        <div>
          <StatusDot state={snapshot.summary.status} pulse />
          <span>
            Last checked {relativeTime(snapshot.summary.observedAt)} at{' '}
            {clockTime(snapshot.summary.observedAt)}
          </span>
        </div>
        <div>
          Next automatic scan {relativeTime(snapshot.nextScheduledCheckAt)}
          <span className="footer-divider">·</span>
          Schema v{snapshot.schemaVersion}
        </div>
      </footer>

      {selected && (
        <DetailSheet
          application={selected}
          history={snapshot.history[selected.application.id] ?? []}
          focusedComponentId={focusedComponentId}
          focusedIncident={snapshot.incidents.find(
            (incident) => incident.id === focusedIncidentId,
          )}
          onClose={closeApplication}
        />
      )}
    </div>
  )
}
